import { disableHoldCleaningMock, isHoldCleaningMockEnabled } from "../mocks/hold-cleaning-mock-mode";
import type {
  HoldCleaningDatasetFreshness,
  HoldCleaningReadiness,
  HoldCleaningSource,
  HoldCleaningSourceState,
} from "../models/hold-cleaning.models";
import { getHoldCleaningImageSource } from "../services/hold-cleaning.service";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { ReactNode, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export const formatHoldCleaningDate = (value?: string | null, includeTime = true) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(date);
};

const sourceLabels: Record<HoldCleaningSource, string> = {
  manual: "Manual",
  form: "Hold Cleaning form",
  snowflake: "Snowflake",
  shipfix: "Shipfix",
  outlook: "Outlook",
  "email-intelligence": "Email Intelligence",
  "image-classification": "Image categories (preview)",
  zeronorth: "Legacy ZeroNorth",
  noaa: "Legacy NOAA",
  "crew-report": "OpsLink Crew Report",
  "legacy-sharepoint": "Legacy SharePoint crew",
};

export const formatHoldCleaningSource = (source: HoldCleaningSource): string => sourceLabels[source];

export const HoldCleaningPanel = ({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={twMerge(
      "rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800",
      className,
    )}
  >
    {(title || action) && (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {title && <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const HoldCleaningMockBanner = () => {
  if (!isHoldCleaningMockEnabled()) return null;
  return (
    <div
      role="status"
      data-test="hold-cleaning-mock-banner"
      className="mb-5 flex flex-wrap items-center justify-between gap-3 border border-amber-400 bg-amber-50 px-4 py-3 text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <div>
        <p className="text-sm font-semibold">Frontend mock data</p>
        <p className="mt-1 text-xs">No Hold Cleaning backend requests or refresh jobs are being created.</p>
      </div>
      <button
        type="button"
        className="border border-amber-700 px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900"
        onClick={disableHoldCleaningMock}
      >
        Return to live data
      </button>
    </div>
  );
};

export const HoldCleaningStatusBadge = ({ value, className }: { value: string; className?: string }) => {
  const normalized = value.toUpperCase();
  const style =
    normalized.includes("FAIL") || normalized === "CRITICAL"
      ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"
      : normalized.includes("WARN") || normalized.includes("PARTIAL") || normalized === "HIGH"
        ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
        : normalized.includes("ACTIVE") ||
            normalized.includes("SUCCESS") ||
            normalized.includes("HEALTH") ||
            normalized === "LOW" ||
            normalized === "APPROVED"
          ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
          : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";

  return (
    <span className={twMerge("inline-flex rounded-full px-2.5 py-1 text-xs font-semibold", style, className)}>
      {value}
    </span>
  );
};

export const HoldCleaningPageState = ({
  title,
  message,
  action,
  tone = "neutral",
}: {
  title: string;
  message: string;
  action?: ReactNode;
  tone?: "neutral" | "error";
}) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={twMerge(
      "flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center",
      tone === "error"
        ? "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
        : "border-gray-300 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200",
    )}
  >
    <h2 className="text-lg font-semibold">{title}</h2>
    <p className="mt-2 max-w-xl text-sm">{message}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const HoldCleaningDatasetBanner = ({ dataset }: { dataset: HoldCleaningDatasetFreshness }) => {
  const hasWarnings = dataset.isPartial || dataset.warnings.length > 0;
  return (
    <div
      role={hasWarnings ? "alert" : "status"}
      className={twMerge(
        "rounded-lg border px-4 py-3",
        hasWarnings
          ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
          : "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100",
      )}
    >
      <div className="flex items-start gap-3">
        {hasWarnings ? (
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none" aria-hidden="true" />
        ) : (
          <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-none" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <span>{hasWarnings ? "Partial source coverage" : "Active source snapshot"}</span>
            <HoldCleaningStatusBadge value={dataset.status} />
          </div>
          <p className="mt-1 text-sm">
            Response generated {formatHoldCleaningDate(dataset.activatedAt)}. Active snapshot {dataset.datasetId}.
          </p>
          {dataset.warnings.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {dataset.warnings.map((warning) => (
                <li key={`${warning.source ?? "dataset"}-${warning.code}`}>
                  {warning.source ? `${formatHoldCleaningSource(warning.source)}: ` : ""}
                  {warning.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export const HoldCleaningSourceStateBadge = ({ state }: { state: HoldCleaningSourceState }) => (
  <HoldCleaningStatusBadge value={state} />
);

export const HoldCleaningReadinessBadge = ({ readiness }: { readiness: HoldCleaningReadiness }) => (
  <HoldCleaningStatusBadge value={readiness.replaceAll("_", " ")} />
);

export const HoldCleaningAuthenticatedImage = ({
  imageId,
  alt,
  preview = false,
}: {
  imageId: string;
  alt: string;
  preview?: boolean;
}) => {
  const [source, setSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let revocableUrl: string | null = null;
    setSource(null);
    setFailed(false);

    getHoldCleaningImageSource(imageId)
      .then((imageSource) => {
        if (!active) {
          if (imageSource.revokeOnDispose) URL.revokeObjectURL(imageSource.url);
          return;
        }
        if (imageSource.revokeOnDispose) revocableUrl = imageSource.url;
        setSource(imageSource.url);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (revocableUrl) URL.revokeObjectURL(revocableUrl);
    };
  }, [imageId]);

  if (failed) {
    return (
      <div className="flex h-48 items-center justify-center bg-gray-100 px-4 text-center text-sm text-gray-600 dark:bg-gray-900 dark:text-gray-300">
        Image content is unavailable.
      </div>
    );
  }

  if (!source) {
    return (
      <div className="flex h-48 animate-pulse items-center justify-center bg-gray-100 text-sm text-gray-500 dark:bg-gray-900">
        Loading image…
      </div>
    );
  }

  return (
    <img
      className={preview ? "max-h-[70vh] w-full object-contain" : "h-48 w-full object-cover"}
      src={source}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
};
